/**
 * Frontend mirror of `Vidhyantra_Backend/rbac/user-grants-satisfy.ts`.
 *
 * @rbac-mirror-revision 7
 */

import { SCOPES, DOMAINS } from './rbac-manifest-stems'

function buildTripleLatticeStems(): Set<string> {
  const out = new Set<string>()
  for (const scope of SCOPES) {
    for (const domain of DOMAINS) {
      out.add(`${scope}.${domain}`)
    }
  }
  return out
}

const TRIPLE_LATTICE_STEMS = buildTripleLatticeStems()

const ACTION_IMPLIES: Record<string, Set<string>> = {
  view: new Set(['view']),
  create: new Set(['view', 'create']),
  edit: new Set(['view', 'edit']),
  delete: new Set(['view', 'delete']),
}

function actionCoversAction(grantAction: string, requiredAction: string): boolean {
  if (grantAction === requiredAction) return true
  const implied = ACTION_IMPLIES[grantAction]
  if (!implied) return false
  return implied.has(requiredAction)
}

type ParsedKey = { kind: 'triple'; scope: string; domain: string; action: string; base: string }

function parseKey(key: string): ParsedKey | null {
  const parts = key.split('.')
  if (parts.length === 3) {
    return {
      kind: 'triple',
      scope: parts[0],
      domain: parts[1],
      action: parts[2],
      base: `${parts[0]}.${parts[1]}`,
    }
  }
  return null
}

/** @deprecated Wildcard grants are not evaluated at runtime. */
export function grantWildcardPatternMatches(_grantPattern: string, _requiredKey: string): boolean {
  return false
}

export function grantSatisfiesRequired(userGrant: string, required: string): boolean {
  if (userGrant.includes('*')) return false
  if (userGrant === required) return true

  const g = parseKey(userGrant)
  const r = parseKey(required)
  if (!g || !r) return false

  if (g.kind === 'triple' && r.kind === 'triple') {
    if (g.base !== r.base) return false
    if (!TRIPLE_LATTICE_STEMS.has(g.base)) return false
    return actionCoversAction(g.action, r.action)
  }

  return false
}

export function userGrantsSatisfy(userGrants: readonly string[], requiredKey: string): boolean {
  for (const g of userGrants) {
    if (!g?.trim() || g.includes('*')) continue
    if (grantSatisfiesRequired(g, requiredKey)) return true
  }
  return false
}

export const _RBAC_ACTION_IMPLIES = ACTION_IMPLIES
